import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import axios from 'axios';
import baseURL from '../../baseURL';
import GirviPageShell from '../common/GirviPageShell';
import { fingerprintService } from '../../services/fingerprintService';

const PledgeFingerprintSearchPage = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState('firstName');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleSearch = async (value, type = null) => {
    try {
      setError('');
      setSearchLoading(true);

      if (type) {
        setSearchType(type);
        setSearchValue(value);
      }

      if (!value.trim()) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/pledges/search`, {
        params: {
          searchType: type || searchType,
          searchValue: value.trim()
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSearchResults(response?.data?.data?.pledges || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Error searching pledges');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleFingerprintSearch = async () => {
    try {
      setError('');
      setSearchLoading(true);
      const result = await fingerprintService.searchPledgesByScan(60);
      const matches = result?.matches || [];

      if (matches.length > 0) {
        const customerCode = matches[0]?.customer?.code;
        if (customerCode) {
          const token = localStorage.getItem('token');
          const resp = await axios.get(`${baseURL}/pledges/client/${customerCode}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const allPledges = resp?.data?.pledges || [];
          const activePledges = allPledges.filter(
            p => p.status !== 'RELEASED' && !p.loanDetails?.released
          );
          setSearchResults(activePledges);
          if (activePledges.length === 0) {
            setError('Fingerprint matched but no active pledges found for this customer.');
          }
        } else {
          setSearchResults(matches);
        }
      } else {
        setSearchResults([]);
        setError('No fingerprint match found. You can still use manual search for pledges without fingerprints.');
      }
    } catch (err) {
      setError(err.message || 'Error searching pledges by fingerprint');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <GirviPageShell
      title="Find by Fingerprint"
      subtitle="Pledge · Search"
      chipLabel="Pledge"
      maxWidth="xl"
     compact>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Search By</InputLabel>
            <Select
              value={searchType}
              onChange={(e) => {
                setSearchType(e.target.value);
                setSearchValue('');
                setSearchResults([]);
                setError('');
              }}
              label="Search By"
            >
              <MenuItem value="firstName">First Name</MenuItem>
              <MenuItem value="lastName">Last Name</MenuItem>
              <MenuItem value="village">Village</MenuItem>
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="mobile">Mobile No</MenuItem>
              <MenuItem value="ticketNo">Ticket Number</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            value={searchValue}
            onChange={(e) => {
              const value = e.target.value;
              setSearchValue(value);
              handleSearch(value);
            }}
            placeholder={searchType === 'ticketNo' ? 'Enter ticket number' : 'Enter value'}
            InputProps={{
              endAdornment: searchLoading ? <CircularProgress size={20} /> : null
            }}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleFingerprintSearch}
            disabled={searchLoading}
            sx={{ height: 36, borderRadius: 2 }}
          >
            Scan Finger
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Pledges ({searchResults.length})
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Ticket No</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Village</TableCell>
              <TableCell>Principal</TableCell>
              <TableCell>Fingerprint</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {searchResults.map((pledge) => (
              <TableRow key={pledge._id} hover>
                <TableCell>{formatDate(pledge.date)}</TableCell>
                <TableCell>{pledge.loanDetails?.ticketNo}</TableCell>
                <TableCell>{`${pledge.customer?.firstName || ''} ${pledge.customer?.lastName || ''}`.trim()}</TableCell>
                <TableCell>{pledge.customer?.mobileNo || '-'}</TableCell>
                <TableCell>{pledge.customer?.village || '-'}</TableCell>
                <TableCell>₹{pledge.loanDetails?.principalAmount?.toLocaleString?.() || 0}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={pledge?.fingerprint?.template ? 'Yes' : 'No'}
                    color={pledge?.fingerprint?.template ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={pledge.loanDetails?.released ? 'Released' : 'Active'}
                    color={pledge.loanDetails?.released ? 'error' : 'success'}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1 }}
                    onClick={() => navigate('/pledge/create-receipt')}
                  >
                    Create Receipt
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate('/release')}
                  >
                    Release
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </GirviPageShell>
  );
};

export default PledgeFingerprintSearchPage;
